import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Ticket, TrendingUp, Clock, RefreshCw, ChevronLeft, ChevronRight, AlertCircle, Calendar } from 'lucide-react';
import { mijneTicketsService } from '../lib/supabaseClient';
import { useTheme } from '../App';
import { formatDateDisplay, formatDateForInput, formatDateForApi, getTodayString } from '../utils/dateUtils';

const MijneticketsDashboard = ({ showNotification, isActive = true }) => {
  const { t } = useTheme();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [scanStats, setScanStats] = useState([]);
  const [salesStats, setSalesStats] = useState([]);
  const [error, setError] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const datePickerRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Check if viewing yesterday (not today)
  const isYesterday = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(selectedDate);
    selected.setHours(0, 0, 0, 0);
    return selected < today;
  }, [selectedDate]);

  // Navigate to previous day
  const goToPreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  // Navigate to today
  const goToToday = () => {
    setSelectedDate(new Date());
    setShowDatePicker(false);
  };

  // Check if can go to next day (not beyond today)
  const canGoNext = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selected = new Date(selectedDate);
    selected.setHours(0, 0, 0, 0);
    return selected < today;
  };

  // Navigate to next day
  const goToNextDay = () => {
    if (canGoNext()) {
      const newDate = new Date(selectedDate);
      newDate.setDate(newDate.getDate() + 1);
      setSelectedDate(newDate);
    }
  };

  // Handle date selection from date picker
  const handleDateSelect = (dateString) => {
    const newDate = new Date(dateString);
    setSelectedDate(newDate);
    setShowDatePicker(false);
  };

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadData = useCallback(async (isAutoRefresh = false) => {
    // Annuleer een eventuele vorige aanvraag
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    // Check if API is configured
    if (!mijneTicketsService.isConfigured()) {
      setError('MijnETickets API is niet geconfigureerd. Voeg je API key en Event ID toe aan het .env bestand.');
      setLoading(false);
      return;
    }

    try {
      if (!isAutoRefresh) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError(null);

      // Get start and end of selected day
      const dayStart = new Date(selectedDate);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(selectedDate);
      dayEnd.setHours(23, 59, 59, 999);

      // Fetch data in parallel: scan stats (10 min interval) and sales stats (hourly)
      const [scansData, salesData] = await Promise.all([
        mijneTicketsService.getScanStats(
          formatDateForApi(dayStart),
          formatDateForApi(dayEnd),
          '10'
        ),
        mijneTicketsService.getSalesStats(
          formatDateForApi(dayStart),
          formatDateForApi(dayEnd),
          'hour'
        )
      ]);

      // Process scan stats - API returns { total_scans, scans_per_interval: { "datetime": count }, cumulative_scans }
      const scansPerInterval = scansData?.scans_per_interval || {};
      const scansArray = Object.entries(scansPerInterval).map(([period, scans]) => ({
        period,
        scans
      }));
      setScanStats(scansArray);

      // Process sales stats
      const salesTickets = salesData?.tickets || [];
      const salesAggregated = {};
      salesTickets.forEach(ticketType => {
        if (ticketType.values && typeof ticketType.values === 'object' && !Array.isArray(ticketType.values)) {
          Object.entries(ticketType.values).forEach(([datetime, count]) => {
            if (!salesAggregated[datetime]) {
              salesAggregated[datetime] = 0;
            }
            salesAggregated[datetime] += count;
          });
        }
      });
      const salesArray = Object.entries(salesAggregated).map(([period, tickets_sold]) => ({
        period,
        tickets_sold
      }));
      setSalesStats(salesArray);

    } catch (err) {
      if (err.name === 'AbortError') return; // Verouderde aanvraag, negeren
      console.error('Error loading MijnETickets data:', err);
      setError(err.message);
      showNotification('Fout bij laden van ticket data: ' + err.message, 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedDate, showNotification]);

  // Load data on mount and when date changes
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh every 60 seconds (only for today)
  useEffect(() => {
    if (autoRefresh && isActive && !isYesterday()) {
      const interval = setInterval(() => {
        loadData(true);
      }, 60000);

      return () => clearInterval(interval);
    }
  }, [autoRefresh, isActive, isYesterday, loadData]);

  const handleManualRefresh = () => {
    loadData();
  };

  // Calculate statistics from scan and sales data
  const calculateStats = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    // Round down to nearest 10 minutes for current interval
    const currentIntervalMinutes = Math.floor(currentMinutes / 10) * 10;

    // Helper to safely parse datetime string (convert "YYYY-MM-DD HH:MM:SS" to "YYYY-MM-DDTHH:MM:SS")
    const parseDateTime = (dateStr) => new Date(dateStr.replace(' ', 'T'));

    // Calculate scans in last 10 minutes
    const scannedLast10Min = !isYesterday() ? scanStats
      .filter(stat => {
        const statTime = parseDateTime(stat.period);
        const statHour = statTime.getHours();
        const statMinutes = statTime.getMinutes();
        return statHour === currentHour && statMinutes === currentIntervalMinutes;
      })
      .reduce((sum, stat) => sum + (stat.scans || 0), 0) : 0;

    // Calculate scans in last hour
    const scannedLastHour = !isYesterday() ? scanStats
      .filter(stat => {
        const statTime = parseDateTime(stat.period);
        return statTime.getHours() === currentHour;
      })
      .reduce((sum, stat) => sum + (stat.scans || 0), 0) : 0;

    // Total scanned today - sum all intervals for the selected day
    const scannedToday = scanStats.reduce((sum, stat) => sum + (stat.scans || 0), 0);

    // Calculate sold tickets from sales stats
    const soldLastHour = !isYesterday() ? salesStats
      .filter(stat => {
        const statHour = new Date(stat.period).getHours();
        return statHour === currentHour;
      })
      .reduce((sum, stat) => sum + (stat.tickets_sold || 0), 0) : 0;

    const soldToday = salesStats.reduce((sum, stat) => sum + (stat.tickets_sold || 0), 0);

    return {
      scannedLast10Min,
      scannedLastHour,
      scannedToday,
      soldLastHour,
      soldToday
    };
  };

  // Calculate hourly breakdown from 10-min scan data
  const getHourlyBreakdown = () => {
    // Helper to safely parse datetime string (convert "YYYY-MM-DD HH:MM:SS" to "YYYY-MM-DDTHH:MM:SS")
    const parseDateTime = (dateStr) => new Date(dateStr.replace(' ', 'T'));

    const hourlyData = [];
    const dayStart = new Date(selectedDate);
    dayStart.setHours(0, 0, 0, 0);

    for (let hour = 0; hour < 24; hour++) {
      // Sum all 10-min intervals for this hour
      const scanned = scanStats
        .filter(stat => {
          const statTime = parseDateTime(stat.period);
          return statTime.getHours() === hour;
        })
        .reduce((sum, stat) => sum + (stat.scans || 0), 0);

      // Get sold tickets from sales stats for this hour
      const sold = salesStats
        .filter(stat => {
          const statHour = parseDateTime(stat.period).getHours();
          return statHour === hour;
        })
        .reduce((sum, stat) => sum + (stat.tickets_sold || 0), 0);

      // Only include hours with data or hours up to now (for today)
      const now = new Date();
      const isToday = selectedDate.toDateString() === now.toDateString();
      const isPastOrCurrent = isToday ? hour <= now.getHours() : true;

      if ((scanned > 0 || sold > 0) || (isPastOrCurrent && hour >= 8 && hour <= 23)) {
        hourlyData.push({
          hour,
          label: `${hour.toString().padStart(2, '0')}:00`,
          scanned,
          sold
        });
      }
    }

    return hourlyData;
  };

  const stats = calculateStats();
  const hourlyData = getHourlyBreakdown();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-zf-accent"></div>
      </div>
    );
  }

  if (error && !mijneTicketsService.isConfigured()) {
    return (
      <div className="space-y-6">
        <h1 className={`text-3xl font-bold ${t.text}`}>Mijnetickets</h1>
        <div className={`${t.card} rounded-xl shadow-lg p-6 border`}>
          <div className="flex items-center gap-4">
            <AlertCircle className="text-yellow-500 w-8 h-8" />
            <div>
              <h2 className={`text-xl font-semibold ${t.text}`}>API Configuratie Vereist</h2>
              <p className={`${t.textSecondary} mt-2`}>
                De MijnETickets API is nog niet geconfigureerd. Voeg de volgende variabelen toe aan je <code className="bg-gray-700 px-2 py-1 rounded">.env</code> bestand:
              </p>
              <pre className={`${t.bg} p-4 rounded-lg mt-4 text-sm overflow-x-auto`}>
                <code>
{`REACT_APP_MIJNETICKETS_API_KEY=je_api_key
REACT_APP_MIJNETICKETS_EVENT_ID=je_event_id`}
                </code>
              </pre>
              <p className={`${t.textMuted} mt-4 text-sm`}>
                Herstart de applicatie na het toevoegen van deze variabelen.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className={`text-3xl font-bold ${t.text}`}>Mijnetickets</h1>
          <p className={`${t.textSecondary} mt-1`}>Ticket verkoop en scan statistieken</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleManualRefresh}
            disabled={refreshing}
            className="bg-zf-accent text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-zf-accent/80 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Ververs
          </button>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 border ${t.border} ${autoRefresh ? 'bg-zf-accent text-white' : `${t.bgSecondary} ${t.text}`} transition-colors`}
          >
            <Clock className="w-4 h-4" />
            Auto ({autoRefresh ? 'aan' : 'uit'})
          </button>
        </div>
      </div>

      {/* Date Navigation */}
      <div className={`${t.card} rounded-xl shadow-lg p-4 border`}>
        <div className="flex items-center justify-between">
          <button
            onClick={goToPreviousDay}
            className={`p-2 rounded-lg ${t.hover} ${t.text}`}
            title="Vorige dag"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <div className="flex items-center gap-4 relative" ref={datePickerRef}>
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${t.hover} transition-colors`}
            >
              <Calendar className={`w-5 h-5 ${t.text}`} />
              <span className={`text-xl font-semibold ${t.text}`}>
                {formatDateDisplay(selectedDate)}
              </span>
            </button>

            {/* Date Picker Dropdown */}
            {showDatePicker && (
              <div
                className={`absolute top-full mt-2 left-1/2 transform -translate-x-1/2 z-50 ${t.card} rounded-xl shadow-xl border p-4`}
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="date"
                  value={formatDateForInput(selectedDate)}
                  max={getTodayString()}
                  onChange={(e) => handleDateSelect(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className={`${t.input} border rounded-lg px-4 py-2 focus:ring-2 focus:ring-zf-accent focus:border-transparent outline-none`}
                />
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      goToToday();
                    }}
                    className="flex-1 bg-zf-accent text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-zf-accent/80 transition-colors"
                  >
                    Vandaag
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const yesterday = new Date();
                      yesterday.setDate(yesterday.getDate() - 1);
                      setSelectedDate(yesterday);
                      setShowDatePicker(false);
                    }}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${t.border} ${t.text} ${t.hover} transition-colors`}
                  >
                    Gisteren
                  </button>
                </div>
              </div>
            )}

            {isYesterday() && (
              <button
                onClick={goToToday}
                className="text-zf-accent hover:text-zf-accent/80 text-sm font-medium"
              >
                Naar vandaag
              </button>
            )}
          </div>

          <button
            onClick={goToNextDay}
            disabled={!canGoNext()}
            className={`p-2 rounded-lg ${canGoNext() ? `${t.hover} ${t.text}` : `${t.textMuted} cursor-not-allowed`}`}
            title="Volgende dag"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Last 10 Minutes Stats (only for today) */}
      {!isYesterday() && (
        <div>
          <h2 className={`text-xl font-semibold ${t.text} mb-4`}>Laatste 10 Minuten</h2>
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
            <StatCard
              title="Gescande Tickets"
              value={stats.scannedLast10Min}
              icon={<Ticket className="w-6 h-6" />}
              color="accent"
            />
          </div>
        </div>
      )}

      {/* Current Hour Stats (only for today) */}
      {!isYesterday() && (
        <div>
          <h2 className={`text-xl font-semibold ${t.text} mb-4`}>Laatste Uur</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <StatCard
              title="Gescande Tickets"
              value={stats.scannedLastHour}
              icon={<Ticket className="w-6 h-6" />}
              color="accent"
            />
            <StatCard
              title="Verkochte Tickets"
              value={stats.soldLastHour}
              icon={<TrendingUp className="w-6 h-6" />}
              color="success"
            />
          </div>
        </div>
      )}

      {/* Day Stats */}
      <div>
        <h2 className={`text-xl font-semibold ${t.text} mb-4`}>
          {isYesterday() ? 'Dagstatistieken' : 'Vandaag Totaal'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <StatCard
            title="Totaal Gescand"
            value={stats.scannedToday}
            icon={<Ticket className="w-6 h-6" />}
            color="accent"
          />
          <StatCard
            title="Totaal Verkocht"
            value={stats.soldToday}
            icon={<TrendingUp className="w-6 h-6" />}
            color="success"
          />
        </div>
      </div>

      {/* Line Chart - Scans per 10 minutes */}
      <div>
        <h2 className={`text-xl font-semibold ${t.text} mb-4`}>Scans cumulatief (per 10 minuten)</h2>
        <div className={`${t.card} rounded-xl shadow-lg p-6 border`}>
          {scanStats.length > 0 ? (
            <div className="relative">
              {/* Legend */}
              <div className="flex gap-6 mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-zf-accent rounded-full"></div>
                  <span className={`text-sm ${t.textSecondary}`}>Gescande tickets (cumulatief)</span>
                </div>
              </div>

              {/* SVG Line Chart */}
              {(() => {
                const parseDateTime = (dateStr) => new Date(dateStr.replace(' ', 'T'));

                // Sort all stats by time
                const sortedStats = [...scanStats].sort((a, b) =>
                  parseDateTime(a.period).getTime() - parseDateTime(b.period).getTime()
                );

                // Find first and last non-zero scan entries
                const firstScanIdx = sortedStats.findIndex(s => s.scans > 0);
                const lastScanIdx = sortedStats.findLastIndex(s => s.scans > 0);

                // Determine time range (default 08:00-17:00 if no scans)
                let startMinutes, endMinutes;
                if (firstScanIdx === -1) {
                  startMinutes = 8 * 60;
                  endMinutes = 17 * 60;
                } else {
                  const firstTime = parseDateTime(sortedStats[firstScanIdx].period);
                  const lastTime = parseDateTime(sortedStats[lastScanIdx].period);
                  // Round start down to hour, end up to next hour
                  startMinutes = Math.floor((firstTime.getHours() * 60 + firstTime.getMinutes()) / 60) * 60;
                  endMinutes = Math.ceil((lastTime.getHours() * 60 + lastTime.getMinutes() + 1) / 60) * 60;
                }

                // Filter stats to visible range
                const visibleStats = sortedStats.filter(stat => {
                  const time = parseDateTime(stat.period);
                  const mins = time.getHours() * 60 + time.getMinutes();
                  return mins >= startMinutes && mins <= endMinutes;
                });

                // Cumulatieve sommen per 10-minuten interval
                let cumScans = 0;
                const cumulativeStats = visibleStats.map(stat => {
                  cumScans += stat.scans || 0;
                  return { ...stat, cumScans };
                });

                const maxScans = Math.max(cumScans, 1);
                const timeRange = endMinutes - startMinutes;
                const chartHeight = 200;
                const chartWidth = 930;
                const offsetX = 50;
                const offsetY = 60;

                // Generate points (cumulatief)
                const points = cumulativeStats.map((stat) => {
                  const time = parseDateTime(stat.period);
                  const mins = time.getHours() * 60 + time.getMinutes();
                  const x = offsetX + ((mins - startMinutes) / timeRange) * chartWidth;
                  const y = offsetY + chartHeight - (stat.cumScans / maxScans) * chartHeight;
                  return { x, y, stat, time };
                });

                const pathD = points.map((p, i) =>
                  `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
                ).join(' ');

                const areaD = points.length > 0
                  ? pathD + ` L ${points[points.length - 1].x} ${offsetY + chartHeight} L ${points[0].x} ${offsetY + chartHeight} Z`
                  : '';

                // Generate x-axis labels (every hour within range)
                const xLabels = [];
                for (let m = startMinutes; m <= endMinutes; m += 60) {
                  xLabels.push(m);
                }

                // Y-axis labels
                const yLabels = [0, 0.25, 0.5, 0.75, 1].map(f => Math.round(maxScans * f));

                return (
                  <div className="relative" style={{ height: '300px' }}>
                    <svg
                      viewBox="0 0 1000 300"
                      className="w-full h-full"
                      preserveAspectRatio="none"
                      onMouseLeave={() => setHoveredPoint(null)}
                    >
                      {/* Grid lines */}
                      {[0, 1, 2, 3, 4].map(i => (
                        <line
                          key={i}
                          x1={offsetX}
                          y1={60 + i * 50}
                          x2={offsetX + chartWidth}
                          y2={60 + i * 50}
                          stroke="currentColor"
                          strokeOpacity="0.1"
                        />
                      ))}

                      {/* X-axis hour labels */}
                      {xLabels.map(m => {
                        const x = offsetX + ((m - startMinutes) / timeRange) * chartWidth;
                        const h = Math.floor(m / 60);
                        return (
                          <text
                            key={m}
                            x={x}
                            y="290"
                            textAnchor="middle"
                            className={`text-xs fill-current ${t.textSecondary}`}
                            style={{ fontSize: '12px' }}
                          >
                            {h.toString().padStart(2, '0')}:00
                          </text>
                        );
                      })}

                      {/* Y-axis labels */}
                      {yLabels.map((val, i) => (
                        <text
                          key={i}
                          x="45"
                          y={265 - i * 50}
                          textAnchor="end"
                          className={`text-xs fill-current ${t.textSecondary}`}
                          style={{ fontSize: '11px' }}
                        >
                          {val}
                        </text>
                      ))}

                      {/* Area fill */}
                      {areaD && <path d={areaD} fill="rgba(233, 69, 96, 0.1)" />}

                      {/* Line */}
                      {pathD && <path d={pathD} fill="none" stroke="#e94560" strokeWidth="2" />}

                      {/* Points */}
                      {points.map((p, i) => (
                        <circle
                          key={i}
                          cx={p.x}
                          cy={p.y}
                          r={hoveredPoint === i ? 6 : 3}
                          fill="#e94560"
                          stroke="white"
                          strokeWidth="1"
                          className="cursor-pointer transition-all"
                          onMouseEnter={() => setHoveredPoint(i)}
                        />
                      ))}
                    </svg>

                    {/* Tooltip */}
                    {hoveredPoint !== null && points[hoveredPoint] && (() => {
                      const p = points[hoveredPoint];
                      const xPercent = ((p.x - offsetX) / chartWidth) * 100;

                      return (
                        <div
                          className={`absolute ${t.card} border shadow-lg rounded-lg px-3 py-2 pointer-events-none z-10`}
                          style={{
                            left: `${Math.min(Math.max(xPercent, 5), 90)}%`,
                            top: '20px',
                            transform: 'translateX(-50%)'
                          }}
                        >
                          <p className={`text-sm font-semibold ${t.text}`}>
                            {p.time.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                          <p className="text-sm text-zf-accent font-bold">
                            {(p.stat.cumScans || 0).toLocaleString('nl-NL')} scans totaal
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}
            </div>
          ) : (
            <p className={`${t.textSecondary} text-center py-8`}>
              Geen data beschikbaar voor deze dag
            </p>
          )}
        </div>
      </div>

      {/* Hourly Summary Table */}
      <div>
        <h2 className={`text-xl font-semibold ${t.text} mb-4`}>Uuroverzicht</h2>
        <div className={`${t.card} rounded-xl shadow-lg p-6 border overflow-x-auto`}>
          {hourlyData.length > 0 ? (
            <table className="w-full">
              <thead>
                <tr className={`border-b ${t.border}`}>
                  <th className={`text-left py-2 px-3 ${t.textSecondary} text-sm font-medium`}>Uur</th>
                  <th className={`text-right py-2 px-3 ${t.textSecondary} text-sm font-medium`}>Gescand</th>
                  <th className={`text-right py-2 px-3 ${t.textSecondary} text-sm font-medium`}>Verkocht</th>
                </tr>
              </thead>
              <tbody>
                {hourlyData.map((hour, index) => (
                  <tr key={index} className={`border-b ${t.border} hover:${t.bgSecondary}`}>
                    <td className={`py-2 px-3 ${t.text}`}>{hour.label}</td>
                    <td className={`py-2 px-3 text-right font-medium text-zf-accent`}>
                      {hour.scanned.toLocaleString('nl-NL')}
                    </td>
                    <td className={`py-2 px-3 text-right font-medium text-green-500`}>
                      {hour.sold.toLocaleString('nl-NL')}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className={`${t.bgSecondary}`}>
                  <td className={`py-2 px-3 font-bold ${t.text}`}>Totaal</td>
                  <td className={`py-2 px-3 text-right font-bold text-zf-accent`}>
                    {hourlyData.reduce((sum, h) => sum + h.scanned, 0).toLocaleString('nl-NL')}
                  </td>
                  <td className={`py-2 px-3 text-right font-bold text-green-500`}>
                    {hourlyData.reduce((sum, h) => sum + h.sold, 0).toLocaleString('nl-NL')}
                  </td>
                </tr>
              </tfoot>
            </table>
          ) : (
            <p className={`${t.textSecondary} text-center py-8`}>
              Geen data beschikbaar voor deze dag
            </p>
          )}
        </div>
      </div>

      {/* Info Panel */}
      <div className={`${t.card} rounded-xl shadow-lg p-4 border`}>
        <div className="flex items-center justify-between">
          <div>
            <p className={`${t.textSecondary} text-sm`}>Data bron</p>
            <p className={`${t.text} font-medium`}>MijnETickets API</p>
          </div>
          <div>
            <p className={`${t.textSecondary} text-sm text-right`}>Laatste update</p>
            <p className={`${t.text} font-medium text-right`}>
              {new Date().toLocaleString('nl-NL')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// StatCard component
const StatCard = ({ title, value, icon, color }) => {
  const { t } = useTheme();

  const colorClasses = {
    success: 'text-green-500',
    error: 'text-red-500',
    accent: 'text-zf-accent',
    accent2: 'text-blue-500',
  };

  return (
    <div className={`${t.card} rounded-xl shadow-lg p-6 border transition-all hover:shadow-xl`}>
      <div className="flex items-center justify-between">
        <div>
          <p className={`${t.textSecondary} text-sm mb-1`}>{title}</p>
          <p className={`${t.text} text-3xl font-bold`}>{value.toLocaleString('nl-NL')}</p>
        </div>
        <div className={colorClasses[color]}>
          {icon}
        </div>
      </div>
    </div>
  );
};

export default MijneticketsDashboard;
